/**
 * HannoNexus — Homepage sections (i18n-aware).
 * Reads text from useHannoContent() hook. Structure/icons remain in hannonexus.js data file.
 */

import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, ChevronDown, Sparkles, Server } from "lucide-react";
import { ECOSYSTEMS, STRATEGIC_PROJECTS, HOW_IT_WORKS, ROADMAP, HXPULSE_FEATURES, IMPACT_STATS, HANNO_PEOPLE } from "@/data/hannonexus";
import { useHannoContent } from "@/data/hanno-content";
import { CountryFlag, FlagCaboVerde, FlagSaoTome } from "@/components/hanno/Flags";

const NEON = "#0A9B5F";
const BSGOTECH_GREEN = "#0A9B5F";

/* ─────────── HERO ─────────── */
export const HannoHero = () => {
  const c = useHannoContent();
  return (
    <section className="relative min-h-[92vh] flex items-center pt-28 lg:pt-32 pb-16 overflow-hidden" data-testid="hanno-hero">
      <div className="absolute inset-0 pointer-events-none">
        <img src="/hanno/hero-main.png" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" style={{ filter: "brightness(1.18) contrast(1.06) saturate(1.05)" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(10,15,26,0.72) 0%, rgba(22,35,63,0.38) 45%, rgba(22,35,63,0.05) 100%)" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,15,26,0.35) 0%, rgba(10,15,26,0) 30%, rgba(10,15,26,0.85) 100%)" }} />
        <div className="absolute inset-0 hx-grid-bg opacity-[0.06]" />
      </div>
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 w-full">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="flex items-center gap-3 mb-6">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border backdrop-blur-xl" style={{ borderColor: `${BSGOTECH_GREEN}66`, background: `${BSGOTECH_GREEN}14` }}>
            <Server size={11} strokeWidth={2.2} style={{ color: BSGOTECH_GREEN }} />
            <span className="text-[10px] uppercase tracking-[0.28em] font-mono font-semibold" style={{ color: BSGOTECH_GREEN }}>{c.brand.powered_by}</span>
          </div>
        </motion.div>
        <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.75, delay: 0.1 }} className="font-display font-bold text-balance tracking-tight leading-[0.98] text-5xl sm:text-6xl lg:text-[92px] text-white" data-testid="hero-title">HannoNexus</motion.h1>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }} className="mt-5 font-display font-light text-2xl sm:text-3xl lg:text-[38px] tracking-tight leading-tight max-w-4xl" style={{ textShadow: "0 1px 2px rgba(10,15,26,0.05)" }}>
          {c.hero.subtitle_p1} <span className="text-hx-neon">{c.hero.subtitle_accent}</span>
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.35 }} className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] uppercase tracking-[0.28em] font-mono text-hx-muted">
          {c.hero.keywords.map((kw, i) => (
            <React.Fragment key={kw}>
              <span>{kw}</span>
              {i < c.hero.keywords.length - 1 && <span className="opacity-40">•</span>}
            </React.Fragment>
          ))}
        </motion.div>
        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.5 }} className="mt-8 max-w-2xl text-[15px] lg:text-base text-hx-text/85 leading-relaxed" style={{ textShadow: "0 1px 2px rgba(10,15,26,0.05)" }}>{c.hero.lede}</motion.p>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.65 }} className="mt-10 flex flex-wrap items-center gap-4">
          <Link to="#ecosystems" data-testid="hero-cta-explore" className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-hx-neon text-hx-bg font-semibold text-[11px] uppercase tracking-[0.18em] transition-all hover:translate-y-[-2px]" style={{ boxShadow: `0 0 24px -4px ${NEON}, 0 8px 30px -10px rgba(0,0,0,0.6)` }}>
            {c.hero.cta_explore}
            <ArrowRight size={14} strokeWidth={2.4} className="group-hover:translate-x-0.5 transition-transform" />
          </Link>
          <Link to="/contact" data-testid="hero-cta-partnerships" className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full border-2 border-hx-neon/50 text-hx-text font-semibold text-[11px] uppercase tracking-[0.18em] transition-all hover:border-hx-neon hover:bg-hx-neon/[0.1] backdrop-blur-xl" style={{ background: "rgba(10,15,26,0.85)" }}>
            {c.hero.cta_partnerships}
            <ArrowUpRight size={13} strokeWidth={2.4} />
          </Link>
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.2 }} className="mt-20 flex items-center gap-2 text-[9px] uppercase tracking-[0.32em] text-hx-muted/70 font-mono">
          <ChevronDown size={12} className="animate-bounce" />{c.hero.scroll_hint}
        </motion.div>
      </div>
    </section>
  );
};

/* ─────────── VISION ─────────── */
export const HannoVision = () => {
  const c = useHannoContent();
  return (
    <section className="relative py-24 lg:py-32 border-t border-white/[0.10]" data-testid="hanno-vision">
      <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center">
        <div className="text-[10px] uppercase tracking-[0.32em] text-hx-neon font-mono font-semibold mb-6">{c.vision.eyebrow}</div>
        <h2 className="font-display font-semibold text-balance tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-[64px]">{c.vision.title} <span className="text-hx-neon">{c.vision.accent}</span></h2>
        <p className="mt-8 text-[15px] lg:text-[17px] text-hx-muted leading-relaxed max-w-3xl mx-auto">{c.vision.p}</p>
      </div>
    </section>
  );
};

/* ─────────── ECOSYSTEMS ─────────── */
export const HannoEcosystems = () => {
  const c = useHannoContent();
  return (
    <section id="ecosystems" className="relative py-20 lg:py-28 bg-gradient-to-b from-transparent to-black/40" data-testid="hanno-ecosystems">
      <div className="absolute inset-0 hx-grid-bg opacity-[0.04] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="max-w-3xl mb-14">
          <div className="text-[10px] uppercase tracking-[0.3em] text-hx-neon font-mono font-semibold">{c.ecosystems.eyebrow}</div>
          <h2 className="mt-4 font-display font-semibold text-3xl sm:text-4xl lg:text-[52px] tracking-tight leading-[1.05]">{c.ecosystems.title} <span className="text-hx-neon">{c.ecosystems.accent}</span></h2>
          <p className="mt-5 text-[14px] lg:text-[15px] text-hx-muted leading-relaxed">{c.ecosystems.lede}</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
          {ECOSYSTEMS.map((eco, i) => {
            const t = c.eco[eco.slug];
            return (
              <motion.div key={eco.slug} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }} whileHover={{ y: -6 }} className="group relative rounded-2xl border overflow-hidden transition-all duration-500" style={{ borderColor: "rgba(10,155,95,0.14)", background: "linear-gradient(180deg, rgba(22,32,51,0.55) 0%, rgba(10,15,26,0.85) 100%)", backdropFilter: "blur(12px)" }} data-testid={`ecosystem-card-${eco.slug}`}>
                <Link to={`/${eco.slug}`} className="block">
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <img src={eco.image} alt={t.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" style={{ filter: "brightness(1.15) contrast(1.05) saturate(1.08)" }} />
                    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,15,26,0) 0%, rgba(10,15,26,0) 50%, rgba(10,15,26,0.55) 100%)" }} />
                    <div className="absolute top-4 left-4 w-11 h-11 rounded-lg flex items-center justify-center backdrop-blur-xl" style={{ background: `linear-gradient(135deg, ${NEON}44 0%, ${NEON}18 70%)`, border: `1px solid ${NEON}88`, boxShadow: `0 0 20px -4px ${NEON}88` }}>
                      <eco.icon size={18} strokeWidth={2} style={{ color: NEON }} />
                    </div>
                    <ArrowUpRight size={16} className="absolute top-5 right-5 text-hx-text/80 group-hover:text-hx-neon group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" strokeWidth={2} />
                    {eco.strategic && (
                      <div className="absolute bottom-4 right-4 px-2.5 py-1 rounded-full backdrop-blur-md"
                        style={{ background: "rgba(212,168,87,0.16)", border: "1px solid rgba(212,168,87,0.55)" }}>
                        <span className="text-[9px] uppercase tracking-[0.22em] font-semibold" style={{ color: "#D4A857" }}>
                          {c.ecosystems.strategic_badge || "Strategic Project"}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-6 lg:p-7">
                    <div className="text-[10px] uppercase tracking-[0.28em] text-hx-neon font-semibold font-mono">{t.tagline}</div>
                    <h3 className="mt-2 font-display font-semibold text-2xl lg:text-[26px] tracking-tight text-hx-text leading-tight">{t.short}</h3>
                    <p className="mt-3 text-[13px] text-hx-muted leading-relaxed">{t.description}</p>
                    <div className="mt-5 pt-5 border-t border-white/[0.10]">
                      <div className="text-[9px] uppercase tracking-[0.26em] text-hx-muted/70 font-mono mb-3">{c.ecosystems.includes}</div>
                      <ul className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                        {eco.subs.slice(0, 6).map((s) => (
                          <li key={s.code} className="flex items-center gap-2 text-[12px] text-hx-text/85">
                            <span className="w-1 h-1 rounded-full shrink-0" style={{ background: NEON, boxShadow: `0 0 6px ${NEON}` }} />{s.code}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="mt-6 pt-4 border-t border-white/[0.10] flex items-center justify-between text-[10px] uppercase tracking-[0.2em]">
                      <span className="text-hx-muted font-mono">/{eco.slug}</span>
                      <span className="text-hx-neon font-semibold group-hover:translate-x-0.5 transition-transform">{c.ecosystems.learn_more} →</span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ─────────── HOW IT WORKS ─────────── */
export const HannoHowItWorks = () => {
  const c = useHannoContent();
  return (
    <section className="relative py-20 lg:py-28 border-t border-white/[0.10]" data-testid="hanno-how">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <div className="text-center mb-14">
          <div className="text-[10px] uppercase tracking-[0.3em] text-hx-neon font-mono font-semibold">{c.how.eyebrow}</div>
          <h2 className="mt-3 font-display font-semibold text-3xl sm:text-4xl lg:text-[48px] tracking-tight leading-[1.05]">{c.how.title} <span className="text-hx-neon">{c.how.accent}</span></h2>
        </div>
        <div className="relative">
          <div className="hidden lg:block absolute left-1/2 top-6 bottom-6 w-px" style={{ background: `linear-gradient(180deg, transparent, ${NEON}88, transparent)` }} />
          <div className="space-y-4 lg:space-y-3">
            {c.how.steps.map((s, i) => (
              <motion.div key={s.step} initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.06 }} className={`relative lg:w-1/2 ${i % 2 === 0 ? "lg:pr-8 lg:text-right" : "lg:ml-auto lg:pl-8"}`}>
                <div className="relative rounded-xl border p-5 lg:p-6" style={{ borderColor: `${NEON}22`, background: "linear-gradient(180deg, rgba(22,32,51,0.5) 0%, rgba(10,15,26,0.85) 100%)" }}>
                  <div className="flex items-center gap-3 mb-1" style={{ justifyContent: i % 2 === 0 ? "flex-end" : "flex-start" }}>
                    <div className="text-[9px] uppercase tracking-[0.3em] font-mono font-bold" style={{ color: NEON }}>{c.how.phase} {String(i + 1).padStart(2, "0")}</div>
                  </div>
                  <h3 className="font-display font-semibold text-lg lg:text-xl tracking-tight text-hx-text">{s.step}</h3>
                  <p className="mt-1 text-[12.5px] text-hx-muted leading-relaxed font-mono">{s.desc}</p>
                </div>
                <div className={`hidden lg:block absolute top-8 w-3 h-3 rounded-full ${i % 2 === 0 ? "-right-1.5" : "-left-1.5"}`} style={{ background: NEON, boxShadow: `0 0 12px ${NEON}` }} />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─────────── STRATEGIC PROJECTS ─────────── */
export const HannoProjects = () => {
  const c = useHannoContent();
  return (
    <section className="relative py-20 lg:py-28 bg-gradient-to-b from-black/40 to-transparent" data-testid="hanno-projects">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="max-w-3xl mb-14">
          <div className="text-[10px] uppercase tracking-[0.3em] text-hx-neon font-mono font-semibold">{c.projects.eyebrow}</div>
          <h2 className="mt-3 font-display font-semibold text-3xl sm:text-4xl lg:text-[52px] tracking-tight leading-[1.05]">{c.projects.title} <span className="text-hx-neon">{c.projects.accent}</span></h2>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          {STRATEGIC_PROJECTS.map((p, i) => {
            const t = c.projects.list[p.slug];
            return (
              <motion.article key={p.slug} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1 }} className="group relative rounded-2xl border overflow-hidden" style={{ borderColor: `${NEON}22`, background: "linear-gradient(180deg, rgba(22,32,51,0.7) 0%, rgba(10,15,26,0.85) 100%)", backdropFilter: "blur(12px)" }} data-testid={`project-${p.slug}`}>
                <div className="relative aspect-[16/8] overflow-hidden">
                  <img src={p.image} alt={t.country} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" style={{ filter: "brightness(1.15) contrast(1.05) saturate(1.08)" }} />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,15,26,0) 0%, rgba(10,15,26,0) 50%, rgba(10,15,26,0.62) 100%)" }} />
                  <div className="absolute top-5 left-5 flex items-center gap-3">
                    <CountryFlag slug={p.slug} className="w-11 h-8 rounded-sm shadow-lg" />
                    <div>
                      <div className="font-display font-semibold text-2xl lg:text-3xl tracking-tight text-hx-text" style={{ textShadow: "0 1px 2px rgba(10,15,26,0.05)" }}>{t.country}</div>
                      <div className="text-[10px] uppercase tracking-[0.24em] text-hx-neon font-mono font-semibold mt-1">{t.focus}</div>
                    </div>
                  </div>
                </div>
                <div className="p-7 lg:p-8">
                  <p className="text-[13.5px] text-hx-text/85 leading-relaxed">{t.tagline}</p>
                  <p className="mt-2 text-[13px] text-hx-muted leading-relaxed">{t.description}</p>
                  <ul className="mt-6 grid grid-cols-2 gap-2">
                    {t.highlights.map((h) => (
                      <li key={h} className="flex items-center gap-2 text-[12px] text-hx-text/85 rounded-md px-3 py-2" style={{ background: `${NEON}0a`, border: `1px solid ${NEON}1f` }}>
                        <span className="w-1 h-1 rounded-full" style={{ background: NEON, boxShadow: `0 0 6px ${NEON}` }} />{h}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ─────────── HX PULSE PLATFORM ─────────── */
export const HannoPulse = () => {
  const c = useHannoContent();
  return (
    <section className="relative py-20 lg:py-28 border-t border-white/[0.10]" data-testid="hanno-pulse">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/4 w-[600px] h-[600px] rounded-full -translate-y-1/2" style={{ background: `radial-gradient(circle, ${NEON}10 0%, transparent 70%)`, filter: "blur(120px)" }} />
      </div>
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-hx-neon font-mono font-semibold">{c.pulse.eyebrow}</div>
            <h2 className="mt-3 font-display font-semibold text-3xl sm:text-4xl lg:text-[52px] tracking-tight leading-[1.05]"><span className="text-hx-neon">{c.pulse.title}</span></h2>
            <p className="mt-5 text-[15px] lg:text-base text-hx-muted leading-relaxed max-w-xl">{c.pulse.lede}</p>
            <ul className="mt-8 grid grid-cols-2 gap-3">
              {HXPULSE_FEATURES.map((f, i) => (
                <li key={f.label} className="flex items-center gap-3 rounded-lg px-3 py-3" style={{ background: `${NEON}08`, border: `1px solid ${NEON}22` }}>
                  <div className="w-8 h-8 rounded-md flex items-center justify-center shrink-0" style={{ background: `${NEON}14`, border: `1px solid ${NEON}55` }}>
                    <f.icon size={14} strokeWidth={2} style={{ color: NEON }} />
                  </div>
                  <span className="text-[12.5px] text-hx-text/90 font-medium">{c.pulse.features[i] || f.label}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="relative rounded-2xl border overflow-hidden p-6 lg:p-8" style={{ borderColor: `${NEON}44`, background: "linear-gradient(180deg, rgba(10,15,26,0.85) 0%, rgba(6,10,18,0.98) 100%)", boxShadow: `0 40px 90px -50px ${NEON}66, 0 0 60px -30px ${NEON}55 inset` }}>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: NEON, boxShadow: `0 0 8px ${NEON}` }} />
                  <span className="text-[9px] uppercase tracking-[0.28em] font-mono font-semibold" style={{ color: NEON }}>Live · HX Pulse</span>
                </div>
                <span className="text-[9px] uppercase tracking-[0.24em] font-mono text-hx-muted">v2.0.1</span>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-4">
                {c.pulse.metrics.map((m) => (
                  <div key={m.k} className="rounded-lg border p-4" style={{ borderColor: `${NEON}1f`, background: `${NEON}06` }}>
                    <div className="text-[9px] uppercase tracking-[0.22em] font-mono text-hx-muted mb-2">{m.k}</div>
                    <div className="font-display font-semibold text-xl lg:text-2xl tracking-tight" style={{ color: NEON }}>{m.v}</div>
                    <div className="text-[10px] text-hx-muted/80 mt-1">{m.sub}</div>
                  </div>
                ))}
              </div>
              <div className="rounded-lg border p-4" style={{ borderColor: `${NEON}1f`, background: `${NEON}06` }}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[9px] uppercase tracking-[0.24em] font-mono text-hx-muted">{c.pulse.chart_label}</span>
                  <span className="text-[9px] font-mono" style={{ color: NEON }}>{c.pulse.chart_status}</span>
                </div>
                <div className="flex items-end gap-1 h-16">
                  {[38, 52, 41, 68, 78, 92, 74, 88, 96, 82, 70, 64, 58, 66, 78, 84, 92, 88, 76, 62, 54, 46, 42, 38].map((h, i) => (
                    <div key={i} className="flex-1 rounded-sm transition-all hover:opacity-100 opacity-70" style={{ height: `${h}%`, background: `linear-gradient(180deg, ${NEON}, ${NEON}55)`, boxShadow: `0 0 8px ${NEON}44` }} />
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─────────── IMPACT ─────────── */
export const HannoImpact = () => {
  const c = useHannoContent();
  return (
    <section className="relative py-20 lg:py-28 border-t border-white/[0.10] overflow-hidden" data-testid="hanno-impact">
      <div className="absolute inset-0 pointer-events-none">
        <img src="/hanno/hx-impact.png" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" loading="lazy" style={{ filter: "brightness(1.18) contrast(1.06) saturate(1.05)" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,15,26,0.62) 0%, rgba(10,15,26,0.45) 50%, rgba(10,15,26,0.78) 100%)" }} />
        <div className="absolute inset-0 hx-grid-bg opacity-[0.05]" />
      </div>
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="max-w-3xl mb-12">
          <div className="text-[10px] uppercase tracking-[0.3em] text-hx-neon font-mono font-semibold">{c.impact.eyebrow}</div>
          <h2 className="mt-3 font-display font-semibold text-3xl sm:text-4xl lg:text-[52px] tracking-tight leading-[1.05]" style={{ textShadow: "0 1px 2px rgba(10,15,26,0.05)" }}>{c.impact.title} <span className="text-hx-neon">{c.impact.accent}</span></h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-3">
          {c.impact.stats.map((s, i) => {
            const Icon = IMPACT_STATS[i]?.icon;
            return (
              <motion.div key={s.label} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }} className="relative rounded-xl border p-6 lg:p-7 overflow-hidden backdrop-blur-xl" style={{ borderColor: `${NEON}33`, background: "linear-gradient(180deg, rgba(10,15,26,0.85) 0%, rgba(6,10,16,0.9) 100%)" }}>
                {Icon && <Icon size={16} strokeWidth={1.8} style={{ color: NEON }} className="mb-4" />}
                <div className="font-display font-bold tracking-tight text-3xl lg:text-4xl" style={{ color: NEON, textShadow: `0 0 24px ${NEON}55` }}>{s.value}</div>
                <div className="mt-2 text-[11px] uppercase tracking-[0.22em] font-mono text-hx-muted leading-relaxed">{s.label}</div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ─────────── ROADMAP ─────────── */
export const HannoRoadmap = () => {
  const c = useHannoContent();
  return (
    <section className="relative py-20 lg:py-28 border-t border-white/[0.10]" data-testid="hanno-roadmap">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <div className="max-w-3xl mb-14">
          <div className="text-[10px] uppercase tracking-[0.3em] text-hx-neon font-mono font-semibold">{c.roadmap.eyebrow}</div>
          <h2 className="mt-3 font-display font-semibold text-3xl sm:text-4xl lg:text-[52px] tracking-tight leading-[1.05]">{c.roadmap.title} <span className="text-hx-neon">{c.roadmap.accent}</span></h2>
        </div>
        <ol className="relative">
          <div className="absolute left-[27px] lg:left-[35px] top-2 bottom-2 w-px" style={{ background: `linear-gradient(180deg, ${NEON}, ${NEON}22)` }} />
          {c.roadmap.items.map((r, i) => (
            <motion.li key={r.year} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }} className="relative pl-16 lg:pl-24 pb-8 last:pb-0">
              <div className="absolute left-0 top-1 flex items-center justify-center rounded-full font-display font-bold text-xs lg:text-sm" style={{ width: 56, height: 56, background: `linear-gradient(135deg, ${NEON}22 0%, transparent 70%)`, border: `2px solid ${NEON}`, color: NEON, boxShadow: `0 0 16px -4px ${NEON}` }}>{r.year}</div>
              <div className="pt-2">
                <h3 className="font-display font-semibold text-xl lg:text-2xl tracking-tight text-hx-text leading-tight">{r.milestone}</h3>
                <p className="mt-2 text-[13px] text-hx-muted leading-relaxed max-w-2xl">{r.desc}</p>
              </div>
            </motion.li>
          ))}
        </ol>
      </div>
    </section>
  );
};

/* ─────────── PEOPLE / TEAMS ─────────── */
export const HannoPeople = () => {
  const c = useHannoContent();
  return (
    <section className="relative py-20 lg:py-28 border-t border-white/[0.10] bg-gradient-to-b from-transparent to-black/30" data-testid="hanno-people">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="max-w-3xl mb-14">
          <div className="text-[10px] uppercase tracking-[0.3em] text-hx-neon font-mono font-semibold">{c.people.eyebrow}</div>
          <h2 className="mt-3 font-display font-semibold text-3xl sm:text-4xl lg:text-[52px] tracking-tight leading-[1.05]">
            {c.people.title} <span className="text-hx-neon">{c.people.accent}</span>
          </h2>
          <p className="mt-5 text-[14px] lg:text-[15px] text-hx-muted leading-relaxed">{c.people.lede}</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
          {HANNO_PEOPLE.map((p, i) => (
            <motion.figure
              key={p.image}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              whileHover={{ y: -4 }}
              className="group relative rounded-2xl border overflow-hidden transition-all"
              style={{ borderColor: `${NEON}22`, background: "linear-gradient(180deg, rgba(22,32,51,0.55) 0%, rgba(10,15,26,0.85) 100%)" }}
              data-testid={`people-card-${i}`}
            >
              <div className="relative aspect-[16/10] overflow-hidden">
                <img src={p.image} alt={c.people.roles[p.role_key]} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" style={{ filter: "brightness(1.15) contrast(1.05) saturate(1.05)" }} />
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,15,26,0) 0%, rgba(10,15,26,0) 55%, rgba(10,15,26,0.62) 100%)" }} />
                <div className="absolute bottom-4 left-4 flex items-center gap-2 px-2.5 py-1 rounded-md backdrop-blur-xl"
                     style={{ background: "rgba(10,15,26,0.85)", border: `1px solid ${NEON}44` }}>
                  <span className="text-base leading-none">{p.flag}</span>
                  <span className="text-[10px] uppercase tracking-[0.22em] font-mono font-semibold text-hx-text">{p.location}</span>
                </div>
              </div>
              <figcaption className="p-5 lg:p-6">
                <div className="text-[9px] uppercase tracking-[0.24em] font-mono font-semibold" style={{ color: NEON }}>
                  {c.people.roles[p.role_key]}
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─────────── PARTNERS ─────────── */
export const HannoPartners = () => {
  const c = useHannoContent();
  return (
    <section className="relative py-20 lg:py-24 border-t border-white/[0.10] bg-gradient-to-b from-black/40 to-transparent" data-testid="hanno-partners">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <div className="max-w-3xl mb-12">
          <div className="text-[10px] uppercase tracking-[0.3em] text-hx-neon font-mono font-semibold">{c.partners.eyebrow}</div>
          <h2 className="mt-3 font-display font-semibold text-3xl sm:text-4xl lg:text-[44px] tracking-tight leading-[1.05]">{c.partners.title} <span className="text-hx-neon">{c.partners.accent}</span></h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {c.partners.categories.map((p, i) => (
            <motion.div key={p} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }} className="rounded-lg border px-4 py-5 text-center transition-colors hover:border-hx-neon/50" style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(10,155,95,0.04)" }}>
              <span className="text-[11.5px] font-medium text-hx-text/90 tracking-wide">{p}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─────────── INVESTMENT / OEM ─────────── */
export const HannoInvestment = () => {
  const c = useHannoContent();
  return (
    <section className="relative py-20 lg:py-28 border-t border-white/[0.10]" data-testid="hanno-invest">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 items-start">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-hx-neon font-mono font-semibold">{c.investment.eyebrow}</div>
            <h2 className="mt-3 font-display font-semibold text-3xl sm:text-4xl lg:text-[48px] tracking-tight leading-[1.05]">{c.investment.title} <span className="text-hx-neon">{c.investment.accent}</span></h2>
            <p className="mt-5 text-[15px] text-hx-muted leading-relaxed">{c.investment.lede}</p>
          </div>
          <ul className="space-y-3">
            {c.investment.advantages.map((a, i) => (
              <motion.li key={a} initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.06 }} className="flex items-start gap-3 rounded-lg border p-4" style={{ borderColor: `${NEON}22`, background: `${NEON}06` }}>
                <div className="w-8 h-8 rounded-md flex items-center justify-center shrink-0 mt-0.5" style={{ background: `${NEON}18`, border: `1px solid ${NEON}55` }}>
                  <Sparkles size={12} strokeWidth={2.2} style={{ color: NEON }} />
                </div>
                <span className="text-[13.5px] text-hx-text/90 leading-relaxed">{a}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

/* ─────────── ABOUT ─────────── */
export const HannoAbout = () => {
  const c = useHannoContent();
  return (
    <section className="relative py-20 lg:py-28 border-t border-white/[0.10] overflow-hidden" data-testid="hanno-about">
      <div className="absolute inset-0 pointer-events-none">
        <img src="/hanno/hx-about.png" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" loading="lazy" style={{ filter: "brightness(1.18) contrast(1.06) saturate(1.05)" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,15,26,0.62) 0%, rgba(10,15,26,0.78) 100%)" }} />
      </div>
      <div className="relative max-w-4xl mx-auto px-5 lg:px-8 text-center">
        <div className="text-[10px] uppercase tracking-[0.3em] text-hx-neon font-mono font-semibold">{c.about.eyebrow}</div>
        <h2 className="mt-4 font-display font-semibold text-3xl sm:text-4xl lg:text-[44px] tracking-tight leading-[1.1]" style={{ textShadow: "0 1px 2px rgba(10,15,26,0.05)" }}>{c.about.title} <span className="text-hx-neon">{c.about.accent}</span></h2>
        <p className="mt-6 text-[15px] lg:text-base text-hx-text/85 leading-relaxed" style={{ textShadow: "0 1px 2px rgba(10,15,26,0.05)" }}>{c.about.p}</p>
      </div>
    </section>
  );
};

/* ─────────── CONTACT BANNER ─────────── */
export const HannoContactBanner = () => {
  const c = useHannoContent();
  return (
    <section className="relative py-24 lg:py-32 overflow-hidden" data-testid="hanno-contact-banner">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 hx-grid-bg opacity-[0.06]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full" style={{ background: `radial-gradient(circle, ${NEON}12 0%, transparent 70%)`, filter: "blur(120px)" }} />
      </div>
      <div className="relative max-w-4xl mx-auto px-5 lg:px-8 text-center">
        <motion.h2 initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="font-display font-semibold tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-[72px]">{c.contact_banner.title} <span className="text-hx-neon">{c.contact_banner.accent}</span></motion.h2>
        <motion.p initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }} className="mt-6 text-[15px] lg:text-base text-hx-muted leading-relaxed max-w-2xl mx-auto">{c.contact_banner.lede}</motion.p>
        <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} className="mt-10 flex flex-wrap items-center justify-center gap-4">
          <Link to="/contact" data-testid="contact-banner-cta" className="group inline-flex items-center gap-2 px-8 py-4 rounded-full bg-hx-neon text-hx-bg font-semibold text-[12px] uppercase tracking-[0.2em] transition-all hover:translate-y-[-2px]" style={{ boxShadow: `0 0 30px -4px ${NEON}` }}>
            {c.contact_banner.cta}
            <ArrowRight size={14} strokeWidth={2.4} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
